
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Download, Filter, Loader2, AlertTriangle, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format, subDays } from 'date-fns';
import { useToast } from '@/components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRange, DateRangePicker } from '@/components/ui/date-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

// Define type for the retention log entries
interface RetentionLog {
  id: string;
  table_name: string;
  action: string;
  record_count: number;
  performed_at: string;
  performed_by: string | null;
  details: {
    state?: string;
    automated?: boolean;
  } | null;
}

// Define date range options
type DateRangeOption = '7days' | '30days' | '90days' | 'custom';

export function RetentionVerificationReport() {
  const { toast } = useToast();
  const [selectedState, setSelectedState] = useState<string | undefined>(undefined);
  const [selectedAction, setSelectedAction] = useState<string | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dateRangeType, setDateRangeType] = useState<DateRangeOption>('30days');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  
  // Get the actual date range based on selection or custom range
  const getDateRange = () => {
    if (dateRangeType === 'custom' && dateRange.from && dateRange.to) {
      return {
        from: dateRange.from,
        to: dateRange.to,
      };
    }

    const now = new Date();
    switch (dateRangeType) {
      case '7days':
        return {
          from: subDays(now, 7),
          to: now,
        };
      case '90days':
        return {
          from: subDays(now, 90),
          to: now,
        };
      case '30days':
      default:
        return {
          from: subDays(now, 30),
          to: now,
        };
    }
  };

  const currentDateRange = getDateRange();
  
  // Fetch retention logs for verification report
  const { data: retentionLogs, isLoading, error } = useQuery({
    queryKey: ['retention-verification', selectedState, selectedAction, dateRangeType, dateRange],
    queryFn: async () => {
      const range = getDateRange();
      const fromDate = range.from.toISOString();
      const toDate = range.to.toISOString();
      
      let query = supabase
        .from('retention_logs')
        .select('*')
        .gte('performed_at', fromDate)
        .lte('performed_at', toDate)
        .order('performed_at', { ascending: false });
      
      if (selectedState) {
        // Filter by state if selected
        query = query.filter('details->state', 'eq', selectedState);
      }
      
      if (selectedAction) {
        // Filter by action if selected
        query = query.eq('action', selectedAction);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as RetentionLog[] || [];
    },
  });
  
  // Fetch list of states for the filter
  const { data: states } = useQuery({
    queryKey: ['states'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('retention_policies')
        .select('state')
        .not('state', 'is', null);
      
      if (error) throw error;
      
      // Extract unique state values and filter out nulls
      const uniqueStates = Array.from(new Set(data.map(row => row.state).filter(Boolean)));
      return uniqueStates as string[];
    },
  });
  
  // Fetch unique actions for the filter
  const { data: actions } = useQuery({
    queryKey: ['retention-actions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('retention_logs')
        .select('action');
      
      if (error) throw error;
      
      // Extract unique action values
      const uniqueActions = Array.from(new Set(data.map(row => row.action)));
      return uniqueActions;
    },
  });
  
  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  // Generate verification report as CSV
  const generateReport = async () => {
    setIsGenerating(true);
    try {
      // In a real implementation, this would generate a proper report file
      // For now, we'll just create a simple CSV from the retention logs
      const headers = [
        'Date', 
        'Table', 
        'Action', 
        'Records', 
        'State', 
        'Execution Type',
        'Performed By'
      ].join(',');
      
      const rows = retentionLogs?.map(log => [
        format(new Date(log.performed_at), 'yyyy-MM-dd HH:mm:ss'),
        log.table_name,
        log.action,
        log.record_count,
        log.details?.state || 'All',
        log.details?.automated ? 'Automated' : 'Manual',
        log.performed_by || 'System'
      ].join(','));
      
      const csvContent = [headers, ...(rows || [])].join('\n');
      
      // Create a blob and download it
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `retention-verification-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Report Generated",
        description: "The verification report has been generated and downloaded",
      });
    } catch (error: any) {
      console.error("Error generating report:", error);
      toast({
        variant: "destructive",
        title: "Failed to generate report",
        description: error.message || "Unknown error occurred",
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Generate a PDF detailed report
  const generateDetailedReport = async () => {
    setIsGenerating(true);
    try {
      // Here we would integrate with a PDF generation service
      // For now, we'll just show a toast notification
      toast({
        title: "Detailed Report Generation",
        description: "PDF report generation feature will be implemented in the next phase",
      });
    } catch (error: any) {
      console.error("Error generating detailed report:", error);
      toast({
        variant: "destructive",
        title: "Failed to generate report",
        description: error.message || "Unknown error occurred",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!retentionLogs) return { total: 0, archived: 0, identified: 0 };
    
    const total = retentionLogs.length;
    const archived = retentionLogs.filter(log => log.action === 'archive').length;
    const identified = retentionLogs.filter(log => log.action === 'identified').length;
    
    return { total, archived, identified };
  };
  
  const stats = getSummaryStats();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Retention Verification Report
          </CardTitle>
          <CardDescription>
            Generate reports to verify retention policy compliance
          </CardDescription>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={generateReport} 
            disabled={isGenerating || !retentionLogs?.length}
            variant="outline" 
            className="gap-2"
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            CSV Report
          </Button>
          
          <Button 
            onClick={generateDetailedReport} 
            disabled={isGenerating || !retentionLogs?.length}
            className="gap-2"
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Detailed Report
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm font-medium">Total Activities</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm font-medium">Archived Records</p>
                  <p className="text-3xl font-bold">{stats.archived}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm font-medium">Identified for Archiving</p>
                  <p className="text-3xl font-bold">{stats.identified}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="logs" className="w-full mb-6">
            <TabsList>
              <TabsTrigger value="logs">Activity Logs</TabsTrigger>
              <TabsTrigger value="compliance">Compliance Status</TabsTrigger>
            </TabsList>
            <TabsContent value="logs">
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={selectedState} onValueChange={setSelectedState}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={undefined}>All States</SelectItem>
                      {states?.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Select value={selectedAction} onValueChange={setSelectedAction}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={undefined}>All Actions</SelectItem>
                      {actions?.map(action => (
                        <SelectItem key={action} value={action}>{action}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {dateRangeType === 'custom' 
                            ? `${format(currentDateRange.from, 'MMM d')} - ${format(currentDateRange.to, 'MMM d')}` 
                            : dateRangeType === '7days' 
                              ? 'Last 7 days'
                              : dateRangeType === '90days'
                                ? 'Last 90 days'
                                : 'Last 30 days'}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <div className="p-4 border-b">
                        <div className="space-y-2">
                          <h4 className="font-medium">Date Range</h4>
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              <Button 
                                variant={dateRangeType === '7days' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => setDateRangeType('7days')}
                                className="flex-1"
                              >
                                Last 7 days
                              </Button>
                              <Button 
                                variant={dateRangeType === '30days' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => setDateRangeType('30days')}
                                className="flex-1"
                              >
                                Last 30 days
                              </Button>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant={dateRangeType === '90days' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => setDateRangeType('90days')}
                                className="flex-1"
                              >
                                Last 90 days
                              </Button>
                              <Button 
                                variant={dateRangeType === 'custom' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => setDateRangeType('custom')}
                                className="flex-1"
                              >
                                Custom
                              </Button>
                            </div>
                          </div>
                          
                          {dateRangeType === 'custom' && (
                            <div className="pt-2">
                              <DateRangePicker 
                                from={dateRange.from} 
                                to={dateRange.to} 
                                onSelect={(range) => {
                                  if (range?.from && range?.to) {
                                    setDateRange(range);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="ml-auto text-sm text-muted-foreground">
                  Showing {retentionLogs?.length || 0} retention actions
                </div>
              </div>
              
              {isLoading ? (
                <div className="py-10 flex justify-center">
                  <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                </div>
              ) : error ? (
                <div className="p-6 text-center">
                  <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-2" />
                  <p>Error loading retention data</p>
                </div>
              ) : retentionLogs && retentionLogs.length > 0 ? (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Table</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Records</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Execution Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {retentionLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>{formatDate(log.performed_at)}</TableCell>
                          <TableCell>{log.table_name}</TableCell>
                          <TableCell>
                            <Badge variant={log.action === 'archive' ? "default" : "outline"}>
                              {log.action === 'archive' ? 'Archived' : 'Identified'}
                            </Badge>
                          </TableCell>
                          <TableCell>{log.record_count}</TableCell>
                          <TableCell>{log.details?.state || 'All'}</TableCell>
                          <TableCell>
                            {log.details?.automated ? (
                              <Badge variant="secondary">Automated</Badge>
                            ) : (
                              <Badge variant="outline">Manual</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-6 text-center border rounded-md">
                  <p className="text-muted-foreground">No retention activities found</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="compliance">
              <div className="p-4 border rounded-md">
                <h3 className="text-lg font-medium mb-4">Retention Compliance Status</h3>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-md bg-muted/50">
                      <h4 className="font-medium mb-2">Archiving Status</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Records identified and archived in the selected period.
                      </p>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm">Identified: <span className="font-medium">{stats.identified}</span></p>
                          <p className="text-sm">Archived: <span className="font-medium">{stats.archived}</span></p>
                        </div>
                        <Badge variant={stats.identified > 0 && stats.archived === 0 ? "destructive" : "success"}>
                          {stats.identified > 0 && stats.archived === 0 ? "Action Required" : "Compliant"}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-md bg-muted/50">
                      <h4 className="font-medium mb-2">Policy Execution</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Scheduled retention policy execution status.
                      </p>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm">Last execution: <span className="font-medium">
                            {retentionLogs && retentionLogs.length > 0 
                              ? formatDate(retentionLogs[0].performed_at)
                              : 'N/A'}
                          </span></p>
                        </div>
                        <Badge variant="outline">Scheduled</Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 border rounded-md bg-muted/50">
                    <h4 className="font-medium mb-2">State-Specific Compliance</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Retention policy compliance by state.
                    </p>
                    <div className="space-y-2 mt-4">
                      {states?.map(state => {
                        const stateActivities = retentionLogs?.filter(
                          log => log.details?.state === state
                        ) || [];
                        const stateArchived = stateActivities.filter(
                          log => log.action === 'archive'
                        ).length;
                        
                        return (
                          <div key={state} className="flex justify-between items-center p-2 border-b">
                            <div className="flex gap-2 items-center">
                              <span className="font-medium">{state}</span>
                              <span className="text-sm text-muted-foreground">
                                {stateActivities.length} activities
                              </span>
                            </div>
                            <Badge variant={stateActivities.length > 0 ? "success" : "outline"}>
                              {stateActivities.length > 0 ? `${stateArchived} archived` : "No activity"}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="bg-muted p-4 rounded-md text-sm">
            <h3 className="font-medium mb-1">Verification Report Contents</h3>
            <p className="text-muted-foreground mb-2">
              The generated report includes detailed records of all retention activities, 
              including archival dates, record counts, and responsible administrators.
              This report can be used for compliance audits and verification purposes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <h4 className="text-xs font-medium uppercase mb-1 text-muted-foreground">CSV Report</h4>
                <ul className="list-disc pl-5 text-sm">
                  <li>Simple tabular format</li>
                  <li>Easy to import into spreadsheet software</li>
                  <li>Contains basic retention activity data</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-medium uppercase mb-1 text-muted-foreground">Detailed PDF Report</h4>
                <ul className="list-disc pl-5 text-sm">
                  <li>Comprehensive compliance status</li>
                  <li>Graphs and visualizations</li>
                  <li>Executive summary and recommendations</li>
                  <li>Ready for regulatory submission</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
